import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dog,
  Plus,
  Users,
  TreeDeciduous,
  Crown,
  Edit,
  Trash2,
  Loader2,
  ChevronDown,
  Baby,
  Heart,
  Award,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FamilyTree = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [myPets, setMyPets] = useState([]);
  const [selectedPet, setSelectedPet] = useState(null);
  const [lineage, setLineage] = useState(null);
  
  // Dialog states
  const [addParentDialogOpen, setAddParentDialogOpen] = useState(false);
  const [addGrandparentDialogOpen, setAddGrandparentDialogOpen] = useState(false);
  const [addOffspringDialogOpen, setAddOffspringDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state for adding parent
  const [parentForm, setParentForm] = useState({
    parent_type: 'sire',
    parent_name: '',
    parent_breed: '',
    parent_color: '',
    parent_birth_year: '',
    parent_registration_number: '',
    parent_titles: ''
  });
  
  // Form state for grandparent
  const [grandparentForm, setGrandparentForm] = useState({
    grandparent_type: 'paternal_grandsire',
    name: '',
    breed: '',
    birth_year: '',
    registration_number: ''
  });

  useEffect(() => {
    if (user) {
      fetchMyPets();
    }
  }, [user]);

  useEffect(() => {
    if (selectedPet) {
      fetchLineage();
    }
  }, [selectedPet]);

  const fetchMyPets = async () => {
    try {
      const response = await axios.get(`${API}/pets/me`);
      setMyPets(response.data);
      if (response.data.length > 0) {
        setSelectedPet(response.data[0]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching pets:', error);
      setLoading(false);
    }
  };

  const fetchLineage = async () => {
    if (!selectedPet) return;
    
    try {
      const response = await axios.get(`${API}/ancestry/${selectedPet.id}`);
      setLineage(response.data);
    } catch (error) {
      console.error('Error fetching lineage:', error);
      setLineage(null);
    }
  };

  const handleAddParent = async () => {
    if (!parentForm.parent_name) {
      toast.error('Please enter a name');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/ancestry/${selectedPet.id}/parent`, {
        pet_id: selectedPet.id,
        parent_type: parentForm.parent_type,
        parent_name: parentForm.parent_name,
        parent_breed: parentForm.parent_breed || null,
        parent_color: parentForm.parent_color || null,
        parent_birth_year: parentForm.parent_birth_year ? parseInt(parentForm.parent_birth_year) : null,
        parent_registration_number: parentForm.parent_registration_number || null,
        parent_titles: parentForm.parent_titles ? parentForm.parent_titles.split(',').map(t => t.trim()) : []
      });
      
      toast.success(`${parentForm.parent_type === 'sire' ? 'Father' : 'Mother'} added!`);
      setAddParentDialogOpen(false);
      resetParentForm();
      fetchLineage();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add parent');
    } finally {
      setSaving(false);
    }
  };

  const handleAddGrandparent = async () => {
    if (!grandparentForm.name) {
      toast.error('Please enter a name');
      return;
    }
    
    setSaving(true);
    try {
      const params = new URLSearchParams({
        grandparent_type: grandparentForm.grandparent_type,
        name: grandparentForm.name
      });
      if (grandparentForm.breed) params.append('breed', grandparentForm.breed);
      if (grandparentForm.birth_year) params.append('birth_year', grandparentForm.birth_year);
      if (grandparentForm.registration_number) params.append('registration_number', grandparentForm.registration_number);
      
      await axios.post(`${API}/ancestry/${selectedPet.id}/grandparent?${params.toString()}`);
      
      toast.success('Grandparent added!');
      setAddGrandparentDialogOpen(false);
      resetGrandparentForm();
      fetchLineage();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add grandparent');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveParent = async (parentType) => {
    try {
      await axios.delete(`${API}/ancestry/${selectedPet.id}/parent/${parentType}`);
      toast.success(`${parentType === 'sire' ? 'Father' : 'Mother'} removed`);
      fetchLineage();
    } catch (error) {
      toast.error('Failed to remove parent');
    }
  };

  const resetParentForm = () => {
    setParentForm({
      parent_type: 'sire',
      parent_name: '',
      parent_breed: '',
      parent_color: '',
      parent_birth_year: '',
      parent_registration_number: '',
      parent_titles: ''
    });
  };

  const resetGrandparentForm = () => {
    setGrandparentForm({
      grandparent_type: 'paternal_grandsire',
      name: '',
      breed: '',
      birth_year: '',
      registration_number: ''
    });
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };

  // Ancestor card component
  const AncestorCard = ({ ancestor, type, canEdit = false, onRemove }) => {
    if (!ancestor) {
      return (
        <div className="bg-muted/30 border-2 border-dashed rounded-lg p-4 text-center min-w-[160px]">
          <div className="w-12 h-12 mx-auto bg-muted rounded-full flex items-center justify-center mb-2">
            <Dog className="w-6 h-6 text-muted-foreground/50" />
          </div>
          <p className="text-xs text-muted-foreground capitalize">{type.replace('_', ' ')}</p>
          <p className="text-sm text-muted-foreground">Unknown</p>
        </div>
      );
    }
    
    return (
      <Card className="min-w-[160px] relative group">
        {canEdit && onRemove && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 h-6 w-6 text-destructive"
            onClick={() => onRemove(type)}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
        <CardContent className="p-3 text-center">
          <Avatar className="w-14 h-14 mx-auto mb-2">
            <AvatarImage src={getImageUrl(ancestor.image_url)} />
            <AvatarFallback>
              <Dog className="w-6 h-6" />
            </AvatarFallback>
          </Avatar>
          <Badge variant="outline" className="text-[10px] mb-1 capitalize">
            {type.replace('_', ' ')}
          </Badge>
          <h4 className="font-medium text-sm">{ancestor.name}</h4>
          {ancestor.breed && (
            <p className="text-xs text-muted-foreground">{ancestor.breed}</p>
          )}
          {ancestor.birth_year && (
            <p className="text-xs text-muted-foreground">Born {ancestor.birth_year}</p>
          )}
          {ancestor.titles?.length > 0 && (
            <div className="flex flex-wrap gap-1 justify-center mt-1">
              {ancestor.titles.slice(0, 2).map((title, i) => (
                <Badge key={i} variant="secondary" className="text-[9px] px-1">
                  <Award className="w-2 h-2 mr-0.5" />
                  {title}
                </Badge>
              ))}
            </div>
          )}
          {ancestor.registration_number && (
            <p className="text-[10px] text-muted-foreground mt-1">
              #{ancestor.registration_number}
            </p>
          )}
        </CardContent>
      </Card>
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md text-center p-8">
          <TreeDeciduous className="w-16 h-16 mx-auto text-green-600 mb-4" />
          <h2 className="text-2xl font-heading mb-2">Pet Family Tree</h2>
          <p className="text-muted-foreground mb-4">
            Sign in to view and build your pet's ancestry
          </p>
          <Button onClick={() => navigate('/login')}>Sign In</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-heading font-medium mb-4 flex items-center gap-3">
            <TreeDeciduous className="w-10 h-10" />
            Family Tree
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Explore and document your pet's lineage. Build a comprehensive family tree
            with parents, grandparents, and offspring.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Pet Selector */}
        {myPets.length > 0 && (
          <Card className="mb-6">
            <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <Label className="text-sm font-medium">Viewing family tree for:</Label>
                <Select value={selectedPet?.id} onValueChange={(id) => setSelectedPet(myPets.find(p => p.id === id))}>
                  <SelectTrigger className="w-[250px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {myPets.map(pet => (
                      <SelectItem key={pet.id} value={pet.id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="w-6 h-6">
                            <AvatarImage src={getImageUrl(pet.image_url)} />
                            <AvatarFallback><Dog className="w-3 h-3" /></AvatarFallback>
                          </Avatar>
                          {pet.name} ({pet.breed || pet.pet_type})
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAddParentDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Parent
                </Button>
                <Button variant="outline" onClick={() => setAddGrandparentDialogOpen(true)}>
                  <Crown className="w-4 h-4 mr-2" />
                  Add Grandparent
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-green-600" />
          </div>
        ) : myPets.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Dog className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="text-xl font-heading mb-2">No Pets Yet</h3>
              <p className="text-muted-foreground mb-4">
                Add a pet to your profile to start building their family tree
              </p>
              <Button onClick={() => navigate('/my-pets')}>Add a Pet</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Family Tree Visualization */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TreeDeciduous className="w-5 h-5" />
                  Pedigree Chart
                </CardTitle>
                <CardDescription>
                  Three generations of {selectedPet?.name}'s lineage
                </CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <div className="min-w-[800px] py-6">
                  {/* Grandparents Row */}
                  <div className="flex justify-center gap-8 mb-4">
                    <div className="flex gap-4">
                      <AncestorCard 
                        ancestor={lineage?.paternal_grandsire} 
                        type="paternal_grandsire" 
                      />
                      <AncestorCard 
                        ancestor={lineage?.paternal_granddam} 
                        type="paternal_granddam" 
                      />
                    </div>
                    <div className="flex gap-4">
                      <AncestorCard 
                        ancestor={lineage?.maternal_grandsire} 
                        type="maternal_grandsire" 
                      />
                      <AncestorCard 
                        ancestor={lineage?.maternal_granddam} 
                        type="maternal_granddam" 
                      />
                    </div>
                  </div>
                  
                  {/* Connection lines */}
                  <div className="flex justify-center gap-[280px] mb-4">
                    <ChevronDown className="w-6 h-6 text-muted-foreground" />
                    <ChevronDown className="w-6 h-6 text-muted-foreground" />
                  </div>
                  
                  {/* Parents Row */}
                  <div className="flex justify-center gap-16 mb-4">
                    <AncestorCard 
                      ancestor={lineage?.sire} 
                      type="sire"
                      canEdit={true}
                      onRemove={handleRemoveParent}
                    />
                    <AncestorCard 
                      ancestor={lineage?.dam} 
                      type="dam"
                      canEdit={true}
                      onRemove={handleRemoveParent}
                    />
                  </div>
                  
                  {/* Connection lines */}
                  <div className="flex justify-center mb-4">
                    <ChevronDown className="w-8 h-8 text-green-600" />
                  </div>
                  
                  {/* Current Pet (Self) */}
                  <div className="flex justify-center">
                    <Card className="min-w-[200px] border-2 border-green-500 shadow-lg">
                      <CardContent className="p-4 text-center">
                        <Avatar className="w-20 h-20 mx-auto mb-3 ring-4 ring-green-500/30">
                          <AvatarImage src={getImageUrl(selectedPet?.image_url)} />
                          <AvatarFallback>
                            <Dog className="w-10 h-10" />
                          </AvatarFallback>
                        </Avatar>
                        <Badge className="bg-green-600 mb-2">Current Pet</Badge>
                        <h3 className="font-heading text-lg font-medium">{selectedPet?.name}</h3>
                        <p className="text-sm text-muted-foreground">{selectedPet?.breed}</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Offspring */}
                  {lineage?.offspring?.length > 0 && (
                    <>
                      <div className="flex justify-center my-4">
                        <ChevronDown className="w-8 h-8 text-blue-500" />
                      </div>
                      <div className="flex justify-center gap-4 flex-wrap">
                        {lineage.offspring.map((child, i) => (
                          <Card key={child.id || i} className="min-w-[140px]">
                            <CardContent className="p-3 text-center">
                              <Avatar className="w-12 h-12 mx-auto mb-2">
                                <AvatarImage src={getImageUrl(child.image_url)} />
                                <AvatarFallback><Baby className="w-5 h-5" /></AvatarFallback>
                              </Avatar>
                              <Badge variant="outline" className="text-[10px] mb-1">
                                Offspring
                              </Badge>
                              <h4 className="font-medium text-sm">{child.name}</h4>
                              {child.breed && (
                                <p className="text-xs text-muted-foreground">{child.breed}</p>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Siblings Section */}
            {lineage?.siblings?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Siblings
                  </CardTitle>
                  <CardDescription>
                    Pets with the same parents as {selectedPet?.name}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4 flex-wrap">
                    {lineage.siblings.map((sibling) => (
                      <Card key={sibling.id} className="min-w-[150px]">
                        <CardContent className="p-3 text-center">
                          <Avatar className="w-14 h-14 mx-auto mb-2">
                            <AvatarImage src={getImageUrl(sibling.image_url)} />
                            <AvatarFallback><Dog className="w-6 h-6" /></AvatarFallback>
                          </Avatar>
                          <h4 className="font-medium">{sibling.name}</h4>
                          <p className="text-xs text-muted-foreground">{sibling.breed}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Add Parent Dialog */}
      <Dialog open={addParentDialogOpen} onOpenChange={setAddParentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Parent</DialogTitle>
            <DialogDescription>
              Add the father (sire) or mother (dam) of {selectedPet?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Parent Type</Label>
              <Select
                value={parentForm.parent_type}
                onValueChange={(v) => setParentForm({ ...parentForm, parent_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sire">Sire (Father)</SelectItem>
                  <SelectItem value="dam">Dam (Mother)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name *</Label>
              <Input
                placeholder="Parent's name"
                value={parentForm.parent_name}
                onChange={(e) => setParentForm({ ...parentForm, parent_name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Breed</Label>
                <Input
                  placeholder="Breed"
                  value={parentForm.parent_breed}
                  onChange={(e) => setParentForm({ ...parentForm, parent_breed: e.target.value })}
                />
              </div>
              <div>
                <Label>Birth Year</Label>
                <Input
                  type="number"
                  placeholder="e.g., 2018"
                  value={parentForm.parent_birth_year}
                  onChange={(e) => setParentForm({ ...parentForm, parent_birth_year: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Registration Number</Label>
              <Input
                placeholder="AKC/Registry number"
                value={parentForm.parent_registration_number}
                onChange={(e) => setParentForm({ ...parentForm, parent_registration_number: e.target.value })}
              />
            </div>
            <div>
              <Label>Titles (comma-separated)</Label>
              <Input
                placeholder="e.g., CH, GCH, Best in Show"
                value={parentForm.parent_titles}
                onChange={(e) => setParentForm({ ...parentForm, parent_titles: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddParentDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddParent} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Parent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Grandparent Dialog */}
      <Dialog open={addGrandparentDialogOpen} onOpenChange={setAddGrandparentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Grandparent</DialogTitle>
            <DialogDescription>
              Add a grandparent to {selectedPet?.name}'s pedigree
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Grandparent Position</Label>
              <Select
                value={grandparentForm.grandparent_type}
                onValueChange={(v) => setGrandparentForm({ ...grandparentForm, grandparent_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paternal_grandsire">Paternal Grandsire (Father's Father)</SelectItem>
                  <SelectItem value="paternal_granddam">Paternal Granddam (Father's Mother)</SelectItem>
                  <SelectItem value="maternal_grandsire">Maternal Grandsire (Mother's Father)</SelectItem>
                  <SelectItem value="maternal_granddam">Maternal Granddam (Mother's Mother)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name *</Label>
              <Input
                placeholder="Grandparent's name"
                value={grandparentForm.name}
                onChange={(e) => setGrandparentForm({ ...grandparentForm, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Breed</Label>
                <Input
                  placeholder="Breed"
                  value={grandparentForm.breed}
                  onChange={(e) => setGrandparentForm({ ...grandparentForm, breed: e.target.value })}
                />
              </div>
              <div>
                <Label>Birth Year</Label>
                <Input
                  type="number"
                  placeholder="e.g., 2010"
                  value={grandparentForm.birth_year}
                  onChange={(e) => setGrandparentForm({ ...grandparentForm, birth_year: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Registration Number</Label>
              <Input
                placeholder="AKC/Registry number"
                value={grandparentForm.registration_number}
                onChange={(e) => setGrandparentForm({ ...grandparentForm, registration_number: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddGrandparentDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddGrandparent} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Grandparent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FamilyTree;
